import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import ColorPicker from './ColorPicker';

const ButtonDialog = ({ open, onClose, onInsert, initialData }) => {
  const defaultData = {
    text: 'Click Here',
    link: '',
    style: 'filled',
    size: 'medium',
    alignment: 'center',
    backgroundColor: '#0F3A61',
    textColor: '#FFFFFF',
    borderRadius: '4',
    fullWidth: false,
    cssClass: '',
  };
  
  const [buttonData, setButtonData] = useState(defaultData);
  
  // Update form when editing existing button
  React.useEffect(() => {
    if (initialData) {
      setButtonData({ ...defaultData, ...initialData });
    } else {
      setButtonData(defaultData);
    }
  }, [initialData, open]);

  const handleChange = (field, value) => {
    setButtonData(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (type, color) => {
    if (type === 'background') {
      handleChange('backgroundColor', color);
    } else if (type === 'color') {
      handleChange('textColor', color);
    }
  };

  const handleInsert = () => {
    // Pass button data object to the custom blot
    onInsert(buttonData);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {initialData ? 'Edit Button' : 'Insert Button'}
        <IconButton
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Button Text */}
          <TextField
            fullWidth
            label="Button Text"
            value={buttonData.text}
            onChange={(e) => handleChange('text', e.target.value)}
            variant="outlined"
          />

          {/* Button Link */}
          <TextField
            fullWidth
            label="Link URL"
            value={buttonData.link}
            onChange={(e) => handleChange('link', e.target.value)}
            placeholder="https://example.com or #section"
            variant="outlined"
            helperText="Enter a URL or anchor link (e.g., #contact)"
          />

          {/* Style and Size Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Style</InputLabel>
              <Select
                value={buttonData.style}
                onChange={(e) => handleChange('style', e.target.value)}
                label="Style"
              >
                <MenuItem value="filled">Filled</MenuItem>
                <MenuItem value="outlined">Outlined</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Size</InputLabel>
              <Select
                value={buttonData.size}
                onChange={(e) => handleChange('size', e.target.value)}
                label="Size"
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Alignment */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Alignment
            </Typography>
            <ToggleButtonGroup
              value={buttonData.alignment}
              exclusive
              onChange={(e, value) => value && handleChange('alignment', value)}
              fullWidth
            >
              <ToggleButton value="left">
                <FormatAlignLeft />
              </ToggleButton>
              <ToggleButton value="center">
                <FormatAlignCenter />
              </ToggleButton>
              <ToggleButton value="right">
                <FormatAlignRight />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Colors */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Background Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorPicker
                  icon={<Box sx={{ width: 24, height: 24, bgcolor: buttonData.backgroundColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                  tooltip="Background Color"
                  value={buttonData.backgroundColor}
                  onChange={handleColorChange}
                  type="background"
                />
                <TextField
                  size="small"
                  value={buttonData.backgroundColor}
                  onChange={(e) => handleChange('backgroundColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Text Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorPicker
                  icon={<Box sx={{ width: 24, height: 24, bgcolor: buttonData.textColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                  tooltip="Text Color"
                  value={buttonData.textColor}
                  onChange={handleColorChange}
                  type="color"
                />
                <TextField
                  size="small"
                  value={buttonData.textColor}
                  onChange={(e) => handleChange('textColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Border Radius and CSS Class Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              sx={{ flex: 1 }}
              label="Border Radius"
              value={buttonData.borderRadius}
              onChange={(e) => handleChange('borderRadius', e.target.value)}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 50 }}
            />
            
            <TextField
              sx={{ flex: 1 }}
              label="CSS Class"
              value={buttonData.cssClass}
              onChange={(e) => handleChange('cssClass', e.target.value)}
              placeholder="btn-primary custom-btn"
              helperText="Space-separated class names"
            />
          </Box>

          {/* Preview */}
          <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            {buttonData.cssClass && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                CSS Classes: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: 2 }}>{buttonData.cssClass}</code>
              </Typography>
            )}
            <Box sx={{ textAlign: buttonData.alignment, mt: 2 }}>
              <Button
                variant={buttonData.style === 'outlined' ? 'outlined' : 'contained'}
                size={buttonData.size}
                sx={{
                  backgroundColor: buttonData.style === 'filled' ? buttonData.backgroundColor : 'transparent',
                  color: buttonData.style === 'outlined' ? buttonData.backgroundColor : buttonData.textColor,
                  borderColor: buttonData.backgroundColor,
                  borderRadius: `${buttonData.borderRadius}px`,
                  borderWidth: buttonData.style === 'outlined' ? 2 : 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: buttonData.style === 'outlined' ? buttonData.backgroundColor : buttonData.backgroundColor,
                    color: buttonData.style === 'outlined' ? buttonData.textColor : buttonData.textColor,
                    opacity: buttonData.style === 'filled' ? 0.9 : 1,
                  },
                }}
              >
                {buttonData.text}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleInsert} 
          variant="contained"
          disabled={!buttonData.text}
        >
          {initialData ? 'Update Button' : 'Insert Button'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonDialog;